
import { overlayHide, overlayShow } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import device from 'bits/device'
import { Component, Prop, Vue } from 'bits/vue'
import FocusTrap from 'library/v4/components/FocusTrap.vue'
import { uniqueId } from 'lodash-es'

@Component({ components: { FocusTrap } })
export default class OzOverlay extends Vue {
  mouseDownTarget: EventTarget | null = null
  mouseUpTarget: EventTarget | null = null

  /**
   * Selects type of scrim. If none is selected, elements covered by the overlay will be fully visible.
   */
  @Prop({ default: 'modal' }) scrim!: 'modal' | 'popover' | null

  /**
   * Dark mode. This has no effect if scrim is turned off.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Fade the overlay and its contents in. Turn this off if you are showing a series of modals.
   */
  @Prop({ default: true }) shouldFadeIn!: boolean

  /**
   * Decides if scrim is not really there and can be clicked through
   */
  @Prop({ default: false }) isSpectral!: boolean

  /**
   *  TailwindCSS z-index class.
   *
   *  - `z-modal2` is for all dialogs
   *  - `z-sidepanel` is for surface sidepanels
   *  - `z-dropdown` is for reaction dropdowns and drafts in surface
   *  - `z-modal-dropdown` is for reaction dropdowns in surface
   */
  @Prop({ default: 'z-modal2' }) zIndexClass!: 'z-modal2' | 'z-sidepanel' | 'z-dropdown' | 'z-modal-dropdown'

  focusTrapDescriptionId = uniqueId('focusTrap')

  get overlayClasses(): (string | Record<string, boolean> | false)[] {
    return [
      // Covers other elements on page
      'fixed',
      'inset-0',
      this.zIndexClass,

      // Transitions
      { fade: this.shouldFadeIn },

      // Scrim
      this.scrim === 'modal' && {
        'bg-modal-overlay-light': this.darkMode === false,
        'bg-modal-overlay-dark': this.darkMode === true,
        'bg-modal-overlay-light dark:bg-modal-overlay-dark': this.darkMode === 'auto',
      },
      this.scrim === 'popover' && {
        'bg-popover-overlay-light': this.darkMode === false,
        'bg-popover-overlay-dark': this.darkMode === true,
        'bg-popover-overlay-light dark:bg-popover-overlay-dark': this.darkMode === 'auto',
      },

      // Misc
      'touch-action-manipulation',
    ]
  }

  get overlayColor(): string {
    // These colors are taken from the TW config.
    if (this.scrim === 'modal') {
      // modal-overlay-dark | modal-overlay-light
      return this.darkMode === true ? 'rgba(36, 37, 39, 0.8)' : 'rgba(0, 0, 0, 0.2)'
    }
    if (this.scrim === 'popover') {
      // popover-overlay-dark | popover-overlay-light
      return this.darkMode === true ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.15)'
    }
    return 'transparent'
  }

  scrimClick(e: MouseEvent): void {
    // Emits if and only if the mouse down and mouse up events
    // are both on the same element and is outside of the overlay.
    // This ensures click and drag between overlay does not hide overlay.
    if (this.mouseDownTarget === this.mouseUpTarget && this.mouseDownTarget === e.target) {
      /**
       * Emitted with overlay scrim is clicked. Can be used to trigger closure of drawer.
       */
      this.$emit('scrim-click')
    }
  }

  scrimEsc(): void {
    /**
     * Emitted when an escape keydown event is propagated to the overlay.
     * Since focus is trapped, it is effectively a global listener.
     */
    this.$emit('scrim-esc')
  }

  mouseDown(e: MouseEvent): void {
    this.mouseDownTarget = e.target
  }

  mouseUp(e: MouseEvent): void {
    this.mouseUpTarget = e.target
  }

  mounted(): void {
    if (device.app && this.scrim !== null) {
      postMessage(
        overlayShow({
          overlayColor: this.overlayColor,
        }),
      )
    }
  }

  beforeDestroy(): void {
    if (device.app && this.scrim !== null) {
      postMessage(overlayHide())
    }
  }
}
